import { Fragment, ReactNode, useCallback, useEffect, useState } from "react";
import { useServerSdk } from "../RootNavigator/services/ServerSdk";
import WithHelp from "../RootNavigator/assistance/Help/WithHelp";
import ArchiveReport, { ArchiveReportProps } from "./ArchiveReport";
import ConsumptionRateParams from "./params/ConsumptionRateParams";
import FinancialOverallParams from "./params/FinancialOverallParams";
import ImpactTrackingParams from "./params/ImpactTrackingParams";
import ProgramImpactParams from "./params/ProgramImpactParams";
import ProjectImpactParams from "./params/ProjectImpactParams";
import { SectionDescriptor, sectionDescriptors } from "./params/utilities";
import ImplementationTrackingParams from "./params/ImplementationTrackingParams";
import OrganizationImpactParams from "./params/OrganizationImpactParams";
import FieldsForm from "@hpo/client/utilities/fields/FieldsForm";
import { useSubmitCallback } from "@hpo/client/utilities/useSubmitCallback";
import Spacer from "@hpo/client/utilities/Spacer";
import Buttons from "@hpo/client/components/Buttons";
import Button from "@hpo/client/components/Button";
import MessageException from "@hpo/client/utilities/errors/MessageException";
import PrintPreviewModal from "@hpo/client/utilities/print/PrintPreviewModal";

import TextField from "@hpo/client/utilities/fields/TextField";
import {
  AnySectionParams,
  ArchiveParams,
  ArchivePrefill,
} from "@hpo/client/models/Archive";
import List, { ListItem } from "@hpo/client/utilities/List";
import SideBar from "@hpo/client/components/SideBar";
import Divider from "@hpo/client/components/Divider";
import FieldLabel from "@hpo/client/utilities/fields/components/FieldLabel";
import T from "@hpo/client/components/text/Text";
import Async from "@hpo/client/utilities/Async";
import ErrorToast from "@hpo/client/utilities/ErrorToast";
import { find } from "@hpo/client/utilities/is";
import Message from "@hpo/client/components/Message";
import Intersperse from "@hpo/client/components/Intersperse";

type ArchiveBuilderProps = {
  prefill: ArchivePrefill | null;
  onPrefilled: () => void;
  onBuilt: () => void;
};

export default function ArchiveBuilder(props: ArchiveBuilderProps) {
  const { prefill, onPrefilled, onBuilt } = props;
  const server = useServerSdk();
  const archives = server.suspender.use(server.getArchives());

  const [title, setTitle] = useState<string | null>(null);
  const [sections, setSections] = useState<Array<AnySectionParams>>([]);

  const [group, setGroup] = useState<string | null>(null);

  useEffect(() => {
    if (prefill && onPrefilled) {
      setSections(prefill.params.sections);
      setGroup(prefill.group);
      onPrefilled();
    }
  }, [prefill, onPrefilled]);

  const [reportProps, setReportProps] = useState<Omit<
    ChartsReportPreviewProps,
    "onClose"
  > | null>(null);

  const [onPreview, preview] = useSubmitCallback(async () => {
    if (title === null) throw new MessageException("Titre manquant", null);
    const params: ArchiveParams = {
      title: title,
      sections: sections,
    };
    const payload = await server.getArchivePayload(params);
    setReportProps({ params, payload, appUrl: window.location.origin });
  }, [title, sections, group]);

  const [onSubmit, submit] = useSubmitCallback(async () => {
    if (title === null) throw new MessageException("Titre manquant", null);
    const params: ArchiveParams = {
      title: title,
      sections: sections,
    };
    await server.buildArchive(params, group);
    onBuilt();
  }, [title, sections, onBuilt, group]);

  const [creation, setCreation] = useState<
    Partial<AnySectionParams> | true | null
  >(null);
  const [edition, setEdition] = useState<AnySectionParams | null>(null);

  const editSection = useCallback((section: AnySectionParams | null) => {
    setEdition(section);
    setCreation(null);
  }, []);

  const createSection = useCallback(
    (section: Partial<AnySectionParams> | true | null) => {
      setEdition(null);
      setCreation(section);
    },
    [],
  );

  const appendSection = useCallback((section: AnySectionParams) => {
    setSections((currentList) => [...currentList, section]);
    editSection(null);
  }, []);

  const replaceSection = useCallback(
    (current: AnySectionParams, section: AnySectionParams) => {
      setSections((currentList) => {
        return currentList.map((s) => (s === current ? section : s));
      });
      editSection(null);
    },
    [],
  );

  const deleteSection = useCallback((section: AnySectionParams) => {
    setSections((currentList) => {
      return currentList.filter((s) => s !== section);
    });
    editSection(null);
  }, []);

  const addSectionNode = (
    <Buttons>
      <Button
        onClick={() => createSection(true)}
        style="discreet"
        label="Ajouter une section"
      />
    </Buttons>
  );

  return (
    <Fragment>
      <WithHelp
        title="Mise en place d'un rapport"
        text={
          <Fragment>
            <T>
              Un rapport est constitué d'un titre et de plusieurs sections.
              Chaque section est un ensemble de tableaux ou de graphiques qui
              restituent les informations de la plateforme
            </T>
            <Spacer />
            <T>
              Vous pouvez ajouter autant de sections que vous le souhaitez, et
              les réorganiser à votre convenance. Certaines sections sont
              paramétrables.
            </T>
            <Spacer />
            <T>
              Vous pouvez réutiliser le paramétrage d'un rapport éxistant
              (depuis l'onglet "Rapports disponibles") pour préremplir cette
              section.
            </T>
          </Fragment>
        }
      />
      <FieldsForm onSubmit={onSubmit}>
        <ErrorToast error={preview.error} />
        <ErrorToast error={submit.error} />
        <Intersperse between={() => <Spacer />}>
          <TextField
            label="Titre du rapport"
            value={title}
            onChange={setTitle}
          />
          <div>
            <FieldLabel text="Sections" />
            <List
              data={sections}
              renderEmpty={() => addSectionNode}
              renderItem={(section) => {
                const desc = find(
                  sectionDescriptors,
                  (s) => s.type === section.type,
                ) as SectionDescriptor<typeof section>;
                return (
                  <Fragment>
                    <ListItem
                      label={section.title}
                      onClick={() => editSection(section)}
                      help={desc.helpers(section)}
                    />
                    <Async>
                      <SideBar
                        visible={edition === section}
                        onHide={() => editSection(null)}
                      >
                        <SectionEditor
                          section={section}
                          onSubmit={(submit) => replaceSection(section, submit)}
                          onDelete={() => deleteSection(section)}
                        />
                      </SideBar>
                    </Async>
                  </Fragment>
                );
              }}
            ></List>
            {sections.length > 0 ? addSectionNode : null}

            <SideBar
              visible={creation !== null}
              onHide={() => createSection(null)}
            >
              {creation === null ? null : creation === true ? (
                <AddSection onSubmit={createSection} />
              ) : (
                <SectionEditor
                  section={creation}
                  onSubmit={appendSection}
                  onDelete={() => createSection(null)}
                />
              )}
            </SideBar>
          </div>
          {group ? (
            <Message>
              <div
                css={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                }}
              >
                <div css={{ flex: 1 }}>
                  <T>
                    Ce rapport constiuera une nouvelle version de "
                    {find(archives, (a) => a.group === group).title}"
                  </T>
                </div>
                <Spacer horizontal />
                <Button
                  style="discreet"
                  icon="close"
                  onClick={() => setGroup(null)}
                />
              </div>
            </Message>
          ) : null}
        </Intersperse>

        <Spacer />
        <Divider />
        <Spacer />

        <Buttons>
          <Button
            onClick={onPreview}
            style="discreet"
            label="Prévisualiser le rapport"
          />
          <Button submit label="Rédiger le rapport" />
        </Buttons>
      </FieldsForm>
      {reportProps ? (
        <ArchiveReportPreview
          {...reportProps}
          key={Math.random()}
          onClose={() => setReportProps(null)}
        />
      ) : null}
    </Fragment>
  );
}

type AddSectionProps = {
  onSubmit: (section: Partial<AnySectionParams>) => void;
};

function AddSection(props: AddSectionProps) {
  const { onSubmit } = props;
  return (
    <div>
      <T style="section">Ajouter une section</T>
      <Spacer />
      <T>
        Sélectionnez le type de section que vous souhaitez ajouter au rapport
      </T>
      <Spacer />
      <List
        data={sectionDescriptors}
        renderItem={(t) => (
          <ListItem
            label={t.title}
            help={t.description}
            onClick={() => onSubmit({ type: t.type })}
          />
        )}
      />
    </div>
  );
}

type SectionEditorProps = {
  section: Partial<AnySectionParams>;
  onSubmit: (section: AnySectionParams) => void;
  onDelete: () => void;
};

function SectionEditor(props: SectionEditorProps) {
  const { section, onSubmit, onDelete } = props;

  let editor: ReactNode = null;

  if (section.type === "consumption-rate") {
    editor = (
      <ConsumptionRateParams
        params={section}
        onChange={onSubmit}
        onDelete={onDelete}
      />
    );
  } else if (section.type === "financial-overall") {
    editor = (
      <FinancialOverallParams
        params={section}
        onChange={onSubmit}
        onDelete={onDelete}
      />
    );
  } else if (section.type === "impact-tracking") {
    editor = (
      <ImpactTrackingParams
        params={section}
        onChange={onSubmit}
        onDelete={onDelete}
      />
    );
  } else if (section.type === "program-impact") {
    editor = (
      <ProgramImpactParams
        params={section}
        onChange={onSubmit}
        onDelete={onDelete}
      />
    );
  } else if (section.type === "project-impact") {
    editor = (
      <ProjectImpactParams
        params={section}
        onChange={onSubmit}
        onDelete={onDelete}
      />
    );
  } else if (section.type === "implementation-tracking") {
    editor = (
      <ImplementationTrackingParams
        params={section}
        onChange={onSubmit}
        onDelete={onDelete}
      />
    );
  } else if (section.type === "organization-impact") {
    editor = (
      <OrganizationImpactParams
        params={section}
        onChange={onSubmit}
        onDelete={onDelete}
      />
    );
  } else {
    throw new Error("Should not hapsssen");
  }

  return <Async>{editor}</Async>;
}

type ChartsReportPreviewProps = ArchiveReportProps & {
  onClose: () => void;
};

function ArchiveReportPreview(props: ChartsReportPreviewProps) {
  const { onClose, ...otherProps } = props;
  return (
    <PrintPreviewModal
      width="29.7cm"
      margin="1cm"
      visible={true}
      onClose={onClose}
    >
      <ArchiveReport {...otherProps} />
    </PrintPreviewModal>
  );
}
